import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import mehak from "../../../../../assets/Mehak.png";
import delhi from "../../../../../assets/indiagate.webp";
import gaziabad from "../../../../../assets/ghaziabad.webp";
import gurugram from "../../../../../assets/gurugram.webp";
import noida from "../../../../../assets/noida.webp";
import { VEHICLE_IMAGES } from "../../../../../Utils/Vehicleimg";
import { GlobalContext } from "../../../../../Context/GlobalContext";
import useGoogleLocationApi from "../../../../Services/Customer/useGoogleLocationApi";
import TrackingOrderLeftPanel from "./TrackingOrderLeftPanel";
import CancelOrderModal from "../CaptainSearchingCard/CancelOrderModal";
import useCustomerDetails from "../../../../Services/Customer/useCustomerDetails";

const TrackingOrderCard = ({
  onNext,
  onBack,
  onCancel,
  formData,
  parcelId,
  selectedVehicle = "Two Wheeler",
  acceptedRide,
  vehicleMeta,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userCode, userID } = useContext(GlobalContext);
  
const { computeDistanceRouteApi, computeDistanceRouteLoading } =
  useGoogleLocationApi();

  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const pickupMarkerRef = useRef(null);
  const dropMarkerRef = useRef(null);
  const driverMarkerRef = useRef(null);
  const routeLineRef = useRef(null);
  const driverLineRef = useRef(null);

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [computedRoutePath, setComputedRoutePath] = useState([]);
  const [routeDistanceText, setRouteDistanceText] = useState("");
  const [routeDurationText, setRouteDurationText] = useState("");

  const selectedCity = location.state?.city || "Delhi";

  const sessionUser = JSON.parse(sessionStorage.getItem("user") || "{}");

  const customerId =
    userCode ||
    userID ||
    formData?.userCode ||
    formData?.userid ||
    formData?.adminId ||
    sessionUser?.userCode ||
    sessionUser?.userid ||
    sessionUser?.userId ||
    sessionUser?.adminId ||
    "";

  const { car, truck, auto, bike } = VEHICLE_IMAGES;

  const cityBanners = {
    Delhi: delhi,
    Gaziabad: gaziabad,
    Gurugram: gurugram,
    Noida: noida,
  };

  const banner = cityBanners[selectedCity] || delhi;

  const acceptedDriver = acceptedRide?.acceptedDriver || {
    id: 1,
    name: "Mehak Aggarwal",
    rating: "4.8",
    distance: "1.5 km",
    eta: "5 mins",
    image: mehak,
  };

  const cancellationReasons = [
    "Changed my mind",
    "Pickup address is wrong",
    "Drop address is wrong",
    "Driver is taking too long",
    "Booked by mistake",
    "Need to change vehicle",
    "Fare is too high",
    "Other reason",
  ];

  const totalPrice = acceptedRide?.totalPrice || 80;
  const selectedExtra = acceptedRide?.selectedExtra || 0;

  const getVehicleIcon = () => {
    const name = String(selectedVehicle || "").toLowerCase();

    if (name.includes("three") || name.includes("auto")) return auto;

    if (
      name.includes("four") ||
      name.includes("eeco") ||
      name.includes("car")
    ) {
      return car;
    }

    if (name.includes("truck") || name.includes("mini")) return truck;

    return bike;
  };

  const vehicleIcon = getVehicleIcon();

  const cleanNumber = (value, fallback) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  };

  const pickupLocation = useMemo(() => {
    return {
      lat: cleanNumber(
        formData?.pickupLatitude ||
          formData?.pickupLat ||
          formData?.pickup_latitude,
        28.6139,
      ),
      lng: cleanNumber(
        formData?.pickupLongitude ||
          formData?.pickupLng ||
          formData?.pickup_longitude,
        77.209,
      ),
    };
  }, [formData]);

  const dropLocation = useMemo(() => {
    return {
      lat: cleanNumber(
        formData?.dropLatitude || formData?.dropLat || formData?.drop_latitude,
        pickupLocation.lat + 0.018,
      ),
      lng: cleanNumber(
        formData?.dropLongitude ||
          formData?.dropLng ||
          formData?.drop_longitude,
        pickupLocation.lng + 0.018,
      ),
    };
  }, [formData, pickupLocation]);

  const driverLocation = useMemo(() => {
    return {
      lat: pickupLocation.lat + 0.006,
      lng: pickupLocation.lng - 0.007,
    };
  }, [pickupLocation]);

  useEffect(() => {
    const fetchComputedRoute = async () => {
      const hasPickup =
        pickupLocation?.lat &&
        pickupLocation?.lng &&
        Number.isFinite(Number(pickupLocation.lat)) &&
        Number.isFinite(Number(pickupLocation.lng));

      const hasDrop =
        dropLocation?.lat &&
        dropLocation?.lng &&
        Number.isFinite(Number(dropLocation.lat)) &&
        Number.isFinite(Number(dropLocation.lng));

      if (!hasPickup || !hasDrop) return;

      const result = await computeDistanceRouteApi({
        originLatitude: String(pickupLocation.lat),
        originLongitude: String(pickupLocation.lng),
        destinationLatitude: String(dropLocation.lat),
        destinationLongitude: String(dropLocation.lng),
        customerId,
      });

      const normalizedRoute = normalizeComputedRoute(result);

      setRouteDistanceText(normalizedRoute.distanceText || "");
      setRouteDurationText(normalizedRoute.durationText || "");

      if (normalizedRoute.path.length > 1) {
        setComputedRoutePath(normalizedRoute.path);
      } else {
        setComputedRoutePath([pickupLocation, dropLocation]);
      }
    };

    fetchComputedRoute();
  }, [
    pickupLocation.lat,
    pickupLocation.lng,
    dropLocation.lat,
    dropLocation.lng,
    customerId,
  ]);

  const normalizeComputedRoute = (apiResponse) => {
    const data =
      apiResponse?.response ||
      apiResponse?.data ||
      apiResponse?.result ||
      apiResponse ||
      {};

    const routeData = Array.isArray(data) ? data[0] : data;

    const encodedPolyline =
      routeData?.encodedPolyline ||
      routeData?.polyline ||
      routeData?.overview_polyline?.points ||
      routeData?.routes?.[0]?.polyline?.encodedPolyline ||
      routeData?.routes?.[0]?.overview_polyline?.points ||
      routeData?.routes?.[0]?.polyline ||
      "";

    const distanceText =
      routeData?.distanceText ||
      routeData?.distance ||
      routeData?.routes?.[0]?.distanceText ||
      routeData?.routes?.[0]?.legs?.[0]?.distance?.text ||
      "";

    const durationText =
      routeData?.durationText ||
      routeData?.duration ||
      routeData?.routes?.[0]?.durationText ||
      routeData?.routes?.[0]?.legs?.[0]?.duration?.text ||
      "";

    const routePoints =
      routeData?.route ||
      routeData?.path ||
      routeData?.points ||
      routeData?.coordinates ||
      routeData?.routes?.[0]?.path ||
      routeData?.routes?.[0]?.points ||
      [];

    let finalPath = [];

    if (
      encodedPolyline &&
      window.google?.maps?.geometry?.encoding?.decodePath
    ) {
      finalPath = window.google.maps.geometry.encoding
        .decodePath(encodedPolyline)
        .map((point) => ({
          lat: point.lat(),
          lng: point.lng(),
        }));
    } else if (Array.isArray(routePoints)) {
      finalPath = routePoints
        .map((point) => {
          const lat =
            point?.lat ||
            point?.latitude ||
            point?.location?.lat ||
            point?.location?.latitude;

          const lng =
            point?.lng ||
            point?.longitude ||
            point?.location?.lng ||
            point?.location?.longitude;

          if (!lat || !lng) return null;

          return {
            lat: Number(lat),
            lng: Number(lng),
          };
        })
        .filter(Boolean);
    }

    return {
      path: finalPath,
      distanceText,
      durationText,
    };
  };

  const randomOtp = useMemo(() => {
    return String(Math.floor(1000 + Math.random() * 9000)).split("");
  }, [acceptedDriver?.id]);

  const registrationNumber = useMemo(() => {
    const registrations = [
      "DL 3C AG 4521",
      "DL 8S BX 9134",
      "HR 26 DK 7845",
      "UP 16 CT 2291",
      "DL 5S AB 6702",
      "HR 55 AQ 3428",
      "UP 14 EQ 9187",
      "DL 1R CN 5046",
    ];

    const driverId = Number(acceptedDriver?.id || 1);
    const index = driverId % registrations.length;

    return registrations[index];
  }, [acceptedDriver?.id]);

  const loadGoogleMap = () => {
    const googleKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    if (!googleKey) {
      console.error("Google Maps API key missing in .env");
      return;
    }

    if (window.google?.maps) {
      initMap();
      return;
    }

    const existingScript = document.getElementById("google-maps-script");

    if (existingScript) {
      existingScript.addEventListener("load", initMap);
      return;
    }

    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleKey}&libraries=geometry`;
    script.async = true;
    script.defer = true;
    script.onload = initMap;

    document.body.appendChild(script);
  };

  const initMap = () => {
    if (!mapRef.current || !window.google?.maps) return;

    if (!googleMapRef.current) {
      googleMapRef.current = new window.google.maps.Map(mapRef.current, {
        center: pickupLocation,
        zoom: 12,
        disableDefaultUI: true,
        zoomControl: true,
        fullscreenControl: true,
        streetViewControl: false,
        mapTypeControl: false,
      });
    }

    renderMap();
  };

  const clearMapItems = () => {
    if (pickupMarkerRef.current) pickupMarkerRef.current.setMap(null);
    if (dropMarkerRef.current) dropMarkerRef.current.setMap(null);
    if (driverMarkerRef.current) driverMarkerRef.current.setMap(null);
    if (routeLineRef.current) routeLineRef.current.setMap(null);
    if (driverLineRef.current) driverLineRef.current.setMap(null);
  };

  const renderMap = () => {
    if (!window.google?.maps || !googleMapRef.current) return;

    const map = googleMapRef.current;

    clearMapItems();

    // const hideInfoWindowCloseButton = (infoWindow) => {
    //   infoWindow.addListener("domready", () => {
    //     const closeButtons = document.querySelectorAll(".gm-ui-hover-effect");

    //     closeButtons.forEach((btn) => {
    //       btn.style.display = "none";
    //     });
    //   });
    // };

    pickupMarkerRef.current = new window.google.maps.Marker({
      map,
      position: pickupLocation,
      title: "Pickup Location",
      label: {
        text: "P",
        color: "#ffffff",
        fontWeight: "700",
      },
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 13,
        fillColor: "#2563eb",
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 4,
      },
    });

    const pickupInfoWindow = new window.google.maps.InfoWindow({
      content: `
    <div style="max-width:220px;font-family:Arial,sans-serif;padding:0 10px 10px 10px;">
      <div style="font-size:15px;font-weight:700;color:#111827;margin-bottom:2px;">
        Pickup Address
      </div>

      <div style="font-size:12px;line-height:17px;color:#4b5563;word-break:break-word;">
        ${
          formData?.pickupAddress ||
          formData?.pickup_location ||
          formData?.pickup ||
          "Pickup Location"
        }
      </div>
    </div>
  `,
    });

    pickupMarkerRef.current.addListener("mouseover", () => {
      pickupInfoWindow.open(map, pickupMarkerRef.current);
    });

    pickupMarkerRef.current.addListener("mouseout", () => {
      pickupInfoWindow.close();
    });

    dropMarkerRef.current = new window.google.maps.Marker({
      map,
      position: dropLocation,
      title: "Drop Location",
      label: {
        text: "D",
        color: "#ffffff",
        fontWeight: "700",
      },
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 13,
        fillColor: "#16a34a",
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 4,
      },
    });

    const dropInfoWindow = new window.google.maps.InfoWindow({
      content: `
    <div style="max-width:220px;font-family:Arial,sans-serif;padding:0 10px 10px 10px;">
      <div style="font-size:15px;font-weight:700;color:#111827;margin-bottom:2px;">
        Drop Address
      </div>

      <div style="font-size:12px;line-height:17px;color:#4b5563;word-break:break-word;">
        ${
          formData?.dropAddress ||
          formData?.drop_location ||
          formData?.drop ||
          "Drop Location"
        }
      </div>
    </div>
  `,
    });

    dropMarkerRef.current.addListener("mouseover", () => {
      dropInfoWindow.open(map, dropMarkerRef.current);
    });

    dropMarkerRef.current.addListener("mouseout", () => {
      dropInfoWindow.close();
    });

    driverMarkerRef.current = new window.google.maps.Marker({
      map,
      position: driverLocation,
      title: acceptedDriver?.name || "Driver",
      icon: {
        url: vehicleIcon,
        scaledSize: new window.google.maps.Size(42, 42),
        anchor: new window.google.maps.Point(21, 21),
      },
    });

    const finalRoutePath =
      computedRoutePath && computedRoutePath.length > 1
        ? computedRoutePath
        : [pickupLocation, dropLocation];

    routeLineRef.current = new window.google.maps.Polyline({
      map,
      path: finalRoutePath,
      geodesic: true,
      strokeColor: "#2563eb",
      strokeOpacity: 0.95,
      strokeWeight: 5,
    });

    driverLineRef.current = new window.google.maps.Polyline({
      map,
      path: [driverLocation, pickupLocation],
      geodesic: true,
      strokeColor: "#f97316",
      strokeOpacity: 0.95,
      strokeWeight: 4,
      icons: [
        {
          icon: {
            path: "M 0,-1 0,1",
            strokeOpacity: 1,
            scale: 4,
          },
          offset: "0",
          repeat: "18px",
        },
      ],
    });

    const driverInfoWindow = new window.google.maps.InfoWindow({
      content: `
        <div style="font-family:Arial; min-width:170px;">
          <b>${acceptedDriver?.name || "Driver"}</b>
          <div style="font-size:12px; margin-top:4px;">
            Rating: ${acceptedDriver?.rating || "-"} ⭐<br/>
            ETA: ${acceptedDriver?.eta || "-"}<br/>
            Vehicle: ${selectedVehicle}
          </div>
        </div>
      `,
    });

    driverMarkerRef.current.addListener("click", () => {
      driverInfoWindow.open(map, driverMarkerRef.current);
    });

    const bounds = new window.google.maps.LatLngBounds();

    bounds.extend(pickupLocation);
    bounds.extend(dropLocation);
    bounds.extend(driverLocation);

    if (computedRoutePath?.length > 1) {
      computedRoutePath.forEach((point) => {
        bounds.extend(point);
      });
    }

    map.fitBounds(bounds);

   setTimeout(() => {
  const zoom = map.getZoom();

  if (zoom && zoom > 15) {
    map.setZoom(15);
  }
}, 300);
  };

  useEffect(() => {
    loadGoogleMap();
  }, []);

  useEffect(() => {
    if (window.google?.maps && googleMapRef.current) {
      renderMap();
    }
  }, [
    pickupLocation,
    dropLocation,
    driverLocation,
    acceptedDriver,
    vehicleIcon,
    computedRoutePath,
  ]);

  const handleCancelConfirm = () => {
    if (!cancelReason) return;

    setShowCancelModal(false);
    setCancelReason("");

    if (onCancel) {
      onCancel({
        reason: cancelReason,
        orderId: parcelId,
        acceptedDriver,
        totalPrice,
      });
      return;
    }

    navigate("/two-wheeler");
  };
  useEffect(() => {
  const handleResize = () => {
    if (googleMapRef.current && window.google?.maps) {
      const center = googleMapRef.current.getCenter();

      window.google.maps.event.trigger(
        googleMapRef.current,
        "resize"
      );

      if (center) {
        googleMapRef.current.setCenter(center);
      }
    }
  };

  window.addEventListener("resize", handleResize);

  return () => {
    window.removeEventListener("resize", handleResize);
  };
}, []);

  return (
    <>
      <section className="w-full px-3 pb-6 pt-8 sm:px-4 md:px-6 md:pt-12 lg:px-0 lg:pt-0">
        {/* BACKGROUND */}
        <div className="image-section absolute left-0 top-0 -z-10 h-[45%] sm:h-[55%] lg:h-[70%] w-full">
          <img
            src={banner}
            alt="bg"
            className="h-full w-full object-cover object-top"
          />
          <div className="image-overlay absolute inset-0 bg-black/30"></div>
        </div>

        <div className="mx-auto mt-6 grid w-full max-w-[1020px] grid-cols-1 overflow-hidden rounded-2xl sm:rounded-[26px] border border-white/80 bg-white shadow-[0_22px_70px_rgba(15,23,42,0.16)] sm:mt-4 md:mt-8 lg:mt-[80px] lg:h-[885px] lg:grid-cols-[57%_43%]">
          {/* LEFT SECTION */}
          <TrackingOrderLeftPanel
            onBack={onBack}
            acceptedDriver={acceptedDriver}
            selectedVehicle={selectedVehicle}
            vehicleMeta={vehicleMeta}
            vehicleIcon={vehicleIcon}
            registrationNumber={registrationNumber}
            randomOtp={randomOtp}
            routeDistanceText={routeDistanceText}
            routeDurationText={routeDurationText}
            formData={formData}
            orderId={parcelId}
            selectedExtra={selectedExtra}
            totalPrice={totalPrice}
            setShowCancelModal={setShowCancelModal}
          />

          {/* RIGHT REAL MAP */}
          <div className="order-1 flex h-[280px] bg-slate-100 p-2 sm:h-[380px] sm:p-3 md:h-[500px] md:p-4 lg:order-2 lg:h-full lg:p-5">
            <div className="relative h-full w-full overflow-hidden rounded-2xl sm:rounded-3xl border border-white/70 bg-white shadow-inner">
              <div
  ref={mapRef}
  className="h-full w-full min-h-[280px]"
/>
            </div>
          </div>
        </div>
      </section>

      <CancelOrderModal
        showCancelModal={showCancelModal}
        cancelReason={cancelReason}
        setCancelReason={setCancelReason}
        setShowCancelModal={setShowCancelModal}
        cancellationReasons={cancellationReasons}
        handleCancelConfirm={handleCancelConfirm}
      />
    </>
  );
};

export default TrackingOrderCard;
